import React, { useEffect, useState, useCallback } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { FaTruck, FaSave, FaPen, FaTrash, FaTimes } from "react-icons/fa";
import { colors, radius, typography, btnSecondary, btnGhost, inputBase, transitions } from "../../utils/designTokens";
import { tolerantId } from "../../utils/firestoreHelpers";
import { CONTADOR_GASTOS, reservarIdSecuencial, mensajeErrorNumeracion } from "../../utils/contadoresFinanzas";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../../utils/demoMode";
import { formatCurrencyUY, formatDateUY } from "../../utils/formatters";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import LoadingState from "../../components/LoadingState";

// Fecha de hoy en formato YYYY-MM-DD (hora local) para inputs type="date"
const getTodayInputDate = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const MIGRATION_DESCRIPTION = "Saldo inicial proveedor — migración";

/**
 * Tab "Saldos Proveedores" en Editar Edificio.
 * Permite cargar el saldo inicial adeudado a cada proveedor del edificio,
 * editando y guardando fila por fila. Cada saldo genera un gasto impago en
 * `expenses` (isInitialBalance: true) que aparece en la pantalla de Gastos.
 */
const SaldosProveedoresTab = ({ buildingData }) => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const buildingId = Number(buildingData?.buildingid);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // id de la fila en edición
  const [editForm, setEditForm] = useState({ amount: "", money: "peso", maturity: getTodayInputDate() });
  const [savingRow, setSavingRow] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const [relationsSnap, suppliersSnap, expensesSnap] = await Promise.all([
        // Vínculos proveedor ↔ edificio de ESTE edificio (el campo es buildingId):
        // sin escanear la colección entera (cláusula TRANSITORIA isAnyStaff de 0D).
        getDocs(query(collection(db, "supplierbuilding"), where("buildingId", "in", tolerantId(buildingId)))),
        getDocs(collection(db, "supplier")),
        getDocs(query(collection(db, "expenses"), where("buildingid", "in", tolerantId(buildingId)))),
      ]);

      const linkedIds = new Set(
        relationsSnap.docs
          .map((d) => d.data())
          .filter((r) => Number(r.buildingId) === buildingId)
          .map((r) => String(r.supplierId))
      );

      const suppliers = suppliersSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => linkedIds.has(String(s.supplierId)))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      const initialExpenses = expensesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((e) => e.isInitialBalance === true);

      setRows(
        suppliers.map((s) => {
          const exp = initialExpenses.find((e) => String(e.supplierid) === String(s.supplierId));
          return {
            id: String(s.supplierId),
            supplierId: s.supplierId,
            name: s.name || "Sin nombre",
            rut: s.rut || "",
            amount: exp ? Number(exp.amount) : null,
            money: exp?.money || "peso",
            maturity: exp?.maturity || "",
            expenseDocId: exp?.id || null,
            expensePaid: !!exp?.paid,
          };
        })
      );
    } catch (err) {
      console.error("Error cargando saldos de proveedores:", err);
      showToast("Error al cargar los proveedores del edificio", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      amount: row.amount != null ? String(row.amount) : "",
      money: row.money || "peso",
      maturity: row.maturity || getTodayInputDate(),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ amount: "", money: "peso", maturity: getTodayInputDate() });
  };

  const handleSaveRow = async (row) => {
    if (savingRow) return;
    const amt = Number(editForm.amount);
    if (!editForm.amount || !isFinite(amt) || amt <= 0) {
      showToast("Ingresá un monto válido mayor a cero", "error");
      return;
    }
    if (!editForm.maturity) {
      showToast("Ingresá la fecha de vencimiento", "error");
      return;
    }

    // Bloquear ANTES de cualquier await para evitar doble click
    setSavingRow(true);
    try {
      if (await isDemoUser()) {
        showToast(DEMO_BLOCK_MESSAGE, "warning");
        return;
      }

      if (row.expenseDocId) {
        await updateDoc(doc(db, "expenses", row.expenseDocId), {
          amount: amt,
          subtotal: amt,
          iva: 0,
          money: editForm.money,
          maturity: editForm.maturity,
        });
        showToast(`Saldo de ${row.name} actualizado.`, "success");
      } else {
        // expensesid del contador atómico (contrato C1): el gasto y el
        // incremento van en la misma transacción.
        const nuevoGastoRef = doc(collection(db, "expenses"));
        const gasto = {
          buildingid: buildingId,
          supplierid: String(row.supplierId),
          hasIncident: false,
          incidents: null,
          receiptNumber: "",
          description: MIGRATION_DESCRIPTION,
          maturity: editForm.maturity,
          invoiceDate: null,
          money: editForm.money,
          subtotal: amt,
          iva: 0,
          amount: amt,
          paid: false,
          bill: null,
          criteria: null,
          dividePorSuperficie: false,
          documentType: "factura",
          expenseCategory: "gastos_comunes",
          projectId: null,
          incidentId: null,
          overrideDistribution: null,
          sourceFund: null,
          isDuplicate: false,
          isInitialBalance: true,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.email || "desconocido",
        };
        const nextId = await reservarIdSecuencial(db, CONTADOR_GASTOS, (tx, expensesid) => {
          tx.set(nuevoGastoRef, { expensesid, ...gasto });
        });
        await addDoc(collection(db, "audit"), {
          action: "create_expense_building",
          buildingId,
          expenseId: nextId,
          detail: "saldo_inicial_proveedor",
          createdBy: auth.currentUser?.email || "desconocido",
          createdAt: serverTimestamp(),
        });
        showToast(`Saldo de ${row.name} guardado. Se creó el gasto impago en Gastos.`, "success");
      }
      cancelEdit();
      await fetchData();
    } catch (err) {
      console.error("Error guardando saldo de proveedor:", err);
      showToast(mensajeErrorNumeracion(err, "saldo") || "Error al guardar el saldo", "error");
    } finally {
      setSavingRow(false);
    }
  };

  const handleDeleteRow = async (row) => {
    if (!row.expenseDocId || deletingId) return;
    const ok = await confirmDialog(
      `¿Eliminar el saldo inicial de ${row.name}? Se eliminará el gasto asociado en Gastos.`,
      { title: "Eliminar saldo inicial", danger: true, confirmLabel: "Eliminar" }
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      if (await isDemoUser()) {
        showToast(DEMO_BLOCK_MESSAGE, "warning");
        return;
      }
      await deleteDoc(doc(db, "expenses", row.expenseDocId));
      showToast(`Saldo de ${row.name} eliminado.`, "success");
      if (editingId === row.id) cancelEdit();
      await fetchData();
    } catch (err) {
      console.error("Error eliminando saldo de proveedor:", err);
      showToast("Error al eliminar el saldo", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const totalPesos = rows.reduce((acc, r) => acc + (r.money === "peso" ? Number(r.amount) || 0 : 0), 0);
  const totalDolares = rows.reduce((acc, r) => acc + (r.money === "dolar" ? Number(r.amount) || 0 : 0), 0);

  const smallInput = { ...inputBase, padding: "8px 10px", fontSize: typography.sizes.base };
  const iconBtn = {
    ...btnGhost,
    padding: 8,
    borderRadius: radius.sm,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: `all ${transitions.fast}`,
  };

  const columns = [
    {
      key: "name",
      label: "Proveedor",
      render: (_, row) => (
        <div>
          <div style={{ fontSize: typography.sizes.md, color: colors.gray900 }}>{row.name}</div>
          {row.rut && <div style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>RUT: {row.rut}</div>}
        </div>
      ),
    },
    {
      key: "amount",
      label: "Saldo inicial",
      width: "150px",
      render: (_, row) =>
        editingId === row.id ? (
          <input
            type="number"
            min="0"
            className="habitia-input"
            style={{ ...smallInput, width: 120 }}
            value={editForm.amount}
            onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
            placeholder="Monto"
            disabled={savingRow}
            aria-label={`Saldo inicial de ${row.name}`}
            autoFocus
          />
        ) : row.amount != null ? (
          <span style={{ fontSize: typography.sizes.md, color: colors.gray900 }}>
            {row.money === "dolar" ? `US$ ${Number(row.amount).toLocaleString("es-UY")}` : formatCurrencyUY(row.amount)}
          </span>
        ) : (
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>—</span>
        ),
    },
    {
      key: "money",
      label: "Moneda",
      width: "120px",
      render: (_, row) =>
        editingId === row.id ? (
          <select
            className="habitia-input"
            style={{ ...smallInput, width: 100 }}
            value={editForm.money}
            onChange={(e) => setEditForm({ ...editForm, money: e.target.value })}
            disabled={savingRow}
            aria-label={`Moneda del saldo de ${row.name}`}
          >
            <option value="peso">$ (UYU)</option>
            <option value="dolar">US$ (USD)</option>
          </select>
        ) : row.amount != null ? (
          <span style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>
            {row.money === "dolar" ? "US$ (USD)" : "$ (UYU)"}
          </span>
        ) : (
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>—</span>
        ),
    },
    {
      key: "maturity",
      label: "Vencimiento",
      width: "165px",
      render: (_, row) =>
        editingId === row.id ? (
          <input
            type="date"
            className="habitia-input"
            style={{ ...smallInput, width: 145 }}
            value={editForm.maturity}
            onChange={(e) => setEditForm({ ...editForm, maturity: e.target.value })}
            disabled={savingRow}
            aria-label={`Vencimiento del saldo de ${row.name}`}
          />
        ) : row.maturity ? (
          <span style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>{formatDateUY(row.maturity)}</span>
        ) : (
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>—</span>
        ),
    },
    {
      key: "estado",
      label: "Estado",
      width: "110px",
      render: (_, row) => {
        if (!row.expenseDocId) return <span style={{ color: colors.gray400, fontSize: typography.sizes.sm }}>Sin gasto</span>;
        return <StatusBadge status={row.expensePaid ? "pagado" : "impago"} size="sm" />;
      },
    },
    {
      key: "acciones",
      label: "Acciones",
      width: "150px",
      align: "right",
      render: (_, row) => {
        if (row.expensePaid) {
          return <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>Pagado</span>;
        }
        if (editingId === row.id) {
          return (
            <div style={{ display: "inline-flex", gap: 4 }}>
              <button
                type="button"
                className="habitia-btn-primary"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  fontSize: typography.sizes.base,
                  fontWeight: 400,
                  color: colors.white,
                  background: colors.primary,
                  border: "none",
                  borderRadius: radius.sm,
                  cursor: savingRow ? "not-allowed" : "pointer",
                  opacity: savingRow ? 0.6 : 1,
                  transition: `all ${transitions.fast}`,
                }}
                onClick={() => handleSaveRow(row)}
                disabled={savingRow}
              >
                <FaSave size={12} />
                {savingRow ? "Guardando…" : "Guardar"}
              </button>
              <button
                type="button"
                style={{ ...iconBtn, opacity: savingRow ? 0.5 : 1 }}
                onClick={cancelEdit}
                disabled={savingRow}
                aria-label="Cancelar edición"
                title="Cancelar"
              >
                <FaTimes size={13} />
              </button>
            </div>
          );
        }
        return (
          <div style={{ display: "inline-flex", gap: 4 }}>
            <button
              type="button"
              className="habitia-btn-secondary"
              style={{
                ...btnSecondary,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                fontSize: typography.sizes.base,
                opacity: editingId || deletingId ? 0.5 : 1,
                cursor: editingId || deletingId ? "not-allowed" : "pointer",
              }}
              onClick={() => startEdit(row)}
              disabled={!!editingId || !!deletingId}
            >
              <FaPen size={11} />
              {row.expenseDocId ? "Editar" : "Cargar"}
            </button>
            {row.expenseDocId && (
              <button
                type="button"
                style={{
                  ...iconBtn,
                  color: colors.error,
                  opacity: editingId || deletingId ? 0.5 : 1,
                  cursor: editingId || deletingId ? "not-allowed" : "pointer",
                }}
                onClick={() => handleDeleteRow(row)}
                disabled={!!editingId || !!deletingId}
                aria-label={`Eliminar saldo de ${row.name}`}
                title="Eliminar"
              >
                <FaTrash size={12} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) return <LoadingState variant="block" message="Cargando proveedores…" />;

  return (
    <div>
      <h3 style={{ fontSize: typography.sizes.lg, color: colors.black, margin: "0 0 12px", fontWeight: 400 }}>
        Saldos Iniciales por Proveedor
      </h3>

      <div
        style={{
          background: colors.infoLight,
          border: `1px solid ${colors.info}`,
          borderRadius: radius.sm,
          padding: 12,
          marginBottom: 16,
          fontSize: typography.sizes.sm,
          color: colors.infoDark,
        }}
      >
        Cargá el saldo que el edificio adeuda a cada proveedor al momento de la migración. Cada saldo se guarda por
        fila y crea un gasto impago en <strong>Gastos</strong> con la descripción "{MIGRATION_DESCRIPTION}". Los saldos
        cuyo gasto ya fue pagado no se pueden modificar desde acá.
      </div>

      <DataTable
        columns={columns}
        data={rows}
        emptyIcon={FaTruck}
        emptyTitle="Este edificio no tiene proveedores asociados"
        emptyDescription="Asociá proveedores al edificio desde la pantalla Proveedores."
      />

      {rows.length > 0 && (totalPesos > 0 || totalDolares > 0) && (
        <div style={{ marginTop: 16, fontSize: typography.sizes.base, color: colors.gray600 }}>
          Total saldos: <span style={{ color: colors.gray900 }}>{formatCurrencyUY(totalPesos)}</span>
          {totalDolares > 0 && <span style={{ color: colors.gray900 }}> + US$ {totalDolares.toLocaleString("es-UY")}</span>}
        </div>
      )}
    </div>
  );
};

export default SaldosProveedoresTab;
